import type { LocationFact } from "@/lib/location-facts";

interface LocationFactsTableProps {
  /** Place name used in the heading and table caption. */
  place: string;
  facts: LocationFact[];
  /** Short line under the heading giving context for the numbers. */
  note?: string;
}

/**
 * Compact "at a glance" facts table shown on every location archive page
 * (city, county, region). Kept as a real <table> so the figures are
 * extractable by search engines and AI answer engines.
 */
export function LocationFactsTable({ place, facts, note }: LocationFactsTableProps) {
  return (
    <section className="border-y border-border bg-muted/30 px-6 py-12 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-accent">At a glance</p>
        <h2 className="mt-2 font-serif text-2xl leading-tight text-brand sm:text-3xl">
          HR outsourcing in {place}: key facts
        </h2>
        {note ? <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{note}</p> : null}
        <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card">
          <table className="w-full border-collapse text-left text-sm">
            <caption className="sr-only">
              Key HR outsourcing facts for {place}: coverage, local business counts, dominant sectors
              and typical monthly retainer
            </caption>
            <tbody>
              {facts.map((fact, i) => (
                <tr
                  key={fact.label}
                  className={i === facts.length - 1 ? "" : "border-b border-border/60"}
                >
                  <th
                    scope="row"
                    className="w-1/2 bg-muted/40 px-5 py-3 align-top text-[11px] font-semibold uppercase tracking-widest text-muted-foreground sm:w-[38%]"
                  >
                    {fact.label}
                  </th>
                  <td className="px-5 py-3 align-top font-medium text-brand">{fact.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Business counts are published by local authority, so neighbouring towns in the same council
          area share the same figure. Retainer bands are guides for planning. Final pricing follows a
          documentation review, because every retainer is insurance-backed.
        </p>
      </div>
    </section>
  );
}
